import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

// Workaround for facebook/react#11538 — browser translation tools (Google
// Translate, Edge Translator, etc.) wrap text nodes in <font>, which breaks
// React's unmount when removeChild/insertBefore find a different parent than
// expected. We swallow only that specific mismatch — every other call goes
// through unchanged.
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (typeof console !== "undefined") {
        console.warn("[react-translate-workaround] removeChild: parent mismatch, skipping", child);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== "undefined") {
        console.warn("[react-translate-workaround] insertBefore: ref parent mismatch, skipping", referenceNode);
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN_CLIENT || "https://0be57f164fd28dd339ea27b6081f1c70@o4511247865217024.ingest.us.sentry.io/4511247894118400",
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  enabled: true,
});

createRoot(document.getElementById("root")!).render(<App />);
